/* eslint-disable react-hooks/set-state-in-effect -- legitimate auth-redirect +
   polling submission stats inside useEffect handlers. */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useContent } from "@/contexts/ContentContext";
import { api } from "@/lib/api";
import FoxMotif from "@/components/site/FoxMotif";
import HeroEditor from "@/components/admin/HeroEditor";
import FeaturedEditor from "@/components/admin/FeaturedEditor";
import PortfolioEditor from "@/components/admin/PortfolioEditor";
import AboutEditor from "@/components/admin/AboutEditor";
import FooterEditor from "@/components/admin/FooterEditor";
import UsersEditor from "@/components/admin/UsersEditor";
import SubmissionsEditor from "@/components/admin/SubmissionsEditor";
import AppPagesEditor from "@/components/admin/AppPagesEditor";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { reset, loading: contentLoading } = useContent();
  const navigate = useNavigate();
  const [tab, setTab] = useState("hero");
  const [resetting, setResetting] = useState(false);
  const [stats, setStats] = useState({ total: 0, new: 0, contact: 0, notify: 0, new_contact: 0, new_notify: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/submissions/stats");
      setStats(res.data);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchStats();
      const t = setInterval(fetchStats, 30000); // refresh every 30s
      return () => clearInterval(t);
    }
  }, [user, fetchStats]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
        <Loader2 className="h-6 w-6 animate-spin text-[#F35A1F]" />
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/admin", { replace: true });
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await reset();
      toast.success("Content reset to defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const newCount = stats.new || 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      <header className="sticky top-0 z-30 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]/95 backdrop-blur">
        <div className="pf-container h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FoxMotif size={32} testId="admin-dashboard-fox" />
            <div>
              <p className="pf-display text-[15px] leading-none">Play Fox <span className="text-[#F35A1F]">Studio</span></p>
              <p className="pf-mono text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Admin dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center rounded-full bg-[#FFEEE6] text-[#D94A14] text-xs font-semibold px-2.5 py-1">
              {user.username}
            </span>
            <Link
              to="/"
              data-testid="admin-dashboard-view-site"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[hsl(var(--border))] bg-white px-3 py-2 text-sm hover:bg-[hsl(var(--accent))]"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="admin-dashboard-reset-button"
                  className="h-9 rounded-xl"
                  disabled={resetting || contentLoading}
                >
                  {resetting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                  Reset content
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset all content to defaults?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will revert every section back to the original Play Fox Studio copy. Your admin accounts, uploaded images, and submissions will not be affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleReset}
                    data-testid="admin-dashboard-reset-confirm"
                    className="bg-[#F35A1F] hover:bg-[#D94A14]"
                  >
                    Reset
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              onClick={handleLogout}
              data-testid="admin-dashboard-logout"
              className="h-9 rounded-xl bg-[#121316] text-white hover:bg-[#1A1C20]"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="pf-container py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="flex flex-wrap h-auto gap-1 bg-[hsl(var(--secondary))] p-1 rounded-2xl mb-6"
            data-testid="admin-tabs"
          >
            <TabsTrigger value="hero" data-testid="admin-tab-hero" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Hero</TabsTrigger>
            <TabsTrigger value="featured" data-testid="admin-tab-featured" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Featured</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="admin-tab-portfolio" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Portfolio</TabsTrigger>
            <TabsTrigger value="app-pages" data-testid="admin-tab-app-pages" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">App Pages</TabsTrigger>
            <TabsTrigger value="about" data-testid="admin-tab-about" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">About</TabsTrigger>
            <TabsTrigger value="footer" data-testid="admin-tab-footer" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Footer</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="admin-tab-submissions" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F] inline-flex items-center gap-1.5">
              Submissions
              {newCount > 0 && (
                <span data-testid="admin-tab-submissions-badge" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F35A1F] text-white text-[10px] font-semibold">
                  {newCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Admins</TabsTrigger>
          </TabsList>

          <div className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 sm:p-8 pf-shadow-sm">
            <TabsContent value="hero" className="m-0"><HeroEditor /></TabsContent>
            <TabsContent value="featured" className="m-0"><FeaturedEditor /></TabsContent>
            <TabsContent value="portfolio" className="m-0"><PortfolioEditor /></TabsContent>
            <TabsContent value="app-pages" className="m-0"><AppPagesEditor /></TabsContent>
            <TabsContent value="about" className="m-0"><AboutEditor /></TabsContent>
            <TabsContent value="footer" className="m-0"><FooterEditor /></TabsContent>
            <TabsContent value="submissions" className="m-0"><SubmissionsEditor onStatsChange={setStats} /></TabsContent>
            <TabsContent value="users" className="m-0"><UsersEditor /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
